/**
 * Panel Utility Module - v2.0
 * 
 * UPDATED: 2025-01-14
 * Provides consistent panel rendering utilities with layout-safe options.
 * Fixes panel width issues in grid/split layouts by controlling container queries.
 * 
 * @module utils/panel
 */

import Config from '../core/Config.js';

/**
 * Panel variant types
 */
export const PANEL_VARIANTS = {
  DEFAULT: 'default',
  COMPACT: 'compact',
  BORDERLESS: 'borderless',
  ELEVATED: 'elevated',
  NESTED: 'nested'
};

/**
 * Panel size options
 */
export const PANEL_SIZES = {
  SMALL: 'sm',
  MEDIUM: 'md',
  LARGE: 'lg',
  FULL: 'full'
};

/**
 * Panel layout types for groups
 */
export const PANEL_LAYOUTS = {
  STACK: 'stack',
  GRID: 'grid',
  SPLIT: 'split'
};

/**
 * Panel behavior types (NEW in v2.0)
 */
export const PANEL_BEHAVIOR = {
  AUTO: 'auto',        // Let CSS automatic rules decide (default)
  LAYOUT_SAFE: 'safe', // Explicitly layout-safe (no container queries)
  STANDALONE: 'standalone' // Can use container queries
};

/**
 * Generate consistent panel HTML with layout safety options
 * 
 * @param {Object} config - Panel configuration
 * @param {string} [config.id] - Panel ID attribute
 * @param {string} [config.title] - Panel header title
 * @param {string} [config.actions] - HTML string for header actions
 * @param {string} [config.content] - Panel body content HTML
 * @param {string} [config.variant='default'] - Panel variant from PANEL_VARIANTS
 * @param {string} [config.size='md'] - Panel size from PANEL_SIZES
 * @param {string} [config.className] - Additional CSS classes
 * @param {Object} [config.attrs] - Additional HTML attributes
 * @param {boolean} [config.collapsible=false] - Whether panel can be collapsed
 * @param {boolean} [config.collapsed=false] - Initial collapsed state
 * @param {boolean} [config.layoutSafe=true] - NEW: Ensure panel is safe for layouts (v2.0)
 * @param {boolean} [config.standalone=false] - NEW: Panel can use container queries (v2.0)
 * @param {boolean} [config.responsiveContent=false] - NEW: Wrap content for responsive behavior (v2.0)
 * @param {boolean} [config.noPadding=false] - NEW: Remove default content padding (v2.1)
 * @returns {string} Panel HTML string
 */
export function createPanel(config = {}) {
  const {
    id = '',
    title = '',
    actions = '',
    content = '',
    variant = PANEL_VARIANTS.DEFAULT,
    size = PANEL_SIZES.MEDIUM,
    className = '',
    attrs = {},
    collapsible = false,
    collapsed = false,
    // NEW v2.0 options
    layoutSafe = true,        // Default to layout-safe for backward compatibility
    standalone = false,       // Opt-in for container queries
    responsiveContent = false, // Wrap content in panel__inner
    noPadding = false,        // Remove default content padding (v2.1)
    // Legacy options
    useUnifiedTheme = Config.PANEL_MIGRATION?.enabled ?? true
  } = config;
  
  // Validate conflicting options
  if (layoutSafe && standalone) {
    console.warn('Panel cannot be both layoutSafe and standalone. Using layoutSafe.');
  }
  
  // Build attributes string
  const attrEntries = Object.entries(attrs);
  const attrString = attrEntries.length > 0 
    ? attrEntries.map(([key, value]) => `${key}="${value}"`).join(' ')
    : '';
  
  // Build class list with new v2.0 classes
  const classList = [
    'panel',
    `panel--${variant}`,
    `panel--${size}`,
    // Add behavior classes (v2.0)
    standalone && !layoutSafe ? 'panel-standalone' : '',
    layoutSafe && !standalone ? 'panel-layout' : '',
    collapsed ? 'panel--collapsed' : '',
    className
  ].filter(Boolean).join(' ');
  
  // Build header if title or actions exist
  const headerHTML = (title || actions) ? `
    <header class="panel__header">
      ${title ? `<h3 class="panel__title">${title}</h3>` : '<div class="panel__title-placeholder"></div>'}
      ${actions ? `<div class="panel__actions">${actions}</div>` : ''}
    </header>
  ` : '';
  
  // Wrap content for responsive behavior if requested (v2.0)
  const innerContent = responsiveContent 
    ? `<div class="panel__inner">${content}</div>`
    : content;
  
  // Build content wrapper with optional no-padding class (v2.1)
  const contentClasses = [
    'panel__content',
    collapsed ? 'panel__content--collapsed' : '',
    noPadding ? 'panel__content--no-padding' : ''
  ].filter(Boolean).join(' ');
  
  const contentHTML = `
    <div class="${contentClasses}">
      ${innerContent}
    </div>
  `;
  
  // Add theme data attribute for CSS targeting  
  const themeAttr = useUnifiedTheme ? 'data-theme="unified"' : '';
  
  // Add behavior data attribute for debugging (v2.0)
  const behaviorAttr = standalone ? 'data-panel-behavior="standalone"' 
    : layoutSafe ? 'data-panel-behavior="layout-safe"' 
    : '';
  
  // Complete panel HTML
  return `
    <div ${id ? `id="${id}"` : ''} class="${classList}" ${themeAttr} ${behaviorAttr} ${attrString}>
      ${headerHTML}
      ${contentHTML}
    </div>
  `;
}

/**
 * Create a panel optimized for grid/split layouts (v2.0 helper)
 * Shorthand for createPanel with layoutSafe: true
 * 
 * @param {Object} config - Same as createPanel but forces layoutSafe
 * @returns {string} Layout-safe panel HTML
 */
export function createLayoutPanel(config = {}) {
  return createPanel({
    ...config,
    layoutSafe: true,
    standalone: false
  });
}

/**
 * Create a standalone panel with container queries (v2.0 helper)
 * Shorthand for createPanel with standalone: true
 * 
 * @param {Object} config - Same as createPanel but forces standalone
 * @returns {string} Standalone panel HTML
 */
export function createStandalonePanel(config = {}) {
  return createPanel({
    ...config,
    layoutSafe: false,
    standalone: true
  });
}

/**
 * Create a group of panels with specified layout
 * Panels in groups are automatically layout-safe (v2.0)
 * 
 * @param {Array<string>} panels - Array of panel HTML strings
 * @param {string} [layout='stack'] - Layout type from PANEL_LAYOUTS
 * @param {string} [className] - Additional CSS classes for the group
 * @returns {string} Panel group HTML string
 */
export function createPanelGroup(panels, layout = PANEL_LAYOUTS.STACK, className = '') {
  if (!Array.isArray(panels) || panels.length === 0) {
    return '';
  }
  
  const layoutClass = `panel-group--${layout}`;
  const classList = ['panel-group', layoutClass, className].filter(Boolean).join(' ');
  
  // Note: Panels inside groups are automatically layout-safe via CSS rules
  return `
    <div class="${classList}">
      ${panels.join('')}
    </div>
  `;
}

/**
 * Build panel header actions from button configurations
 * 
 * @param {Array<Object>} buttons - Array of button configurations
 * @param {string} buttons[].label - Button text label
 * @param {string} buttons[].action - Data-action attribute value
 * @param {string} [buttons[].icon] - Optional icon HTML/text
 * @param {string} [buttons[].variant='ghost'] - Button variant (primary, secondary, ghost, danger)
 * @param {string} [buttons[].size='small'] - Button size (small, medium, large)
 * @param {Object} [buttons[].attrs] - Additional button attributes
 * @returns {string} Actions HTML string
 */
export function createPanelActions(buttons = []) {
  if (!Array.isArray(buttons) || buttons.length === 0) {
    return '';
  }
  
  return buttons.map(btn => {
    const { 
      label = '', 
      action = '', 
      icon = '', 
      variant = 'ghost', 
      size = 'small',
      attrs = {}
    } = btn;
    
    // Build attributes
    const attrString = Object.entries(attrs)
      .map(([key, value]) => `${key}="${value}"`)
      .join(' ');
    
    // Build button HTML
    return `
      <button class="btn-${variant} btn-${size}" data-action="${action}" ${attrString}>
        ${icon ? `<span class="panel__action-icon">${icon}</span>` : ''}
        ${label ? `<span class="panel__action-label">${label}</span>` : ''}
      </button>
    `;
  }).join('');
}

/**
 * Create a panel header with title and actions
 * Useful for updating just the header content
 * 
 * @param {string} title - Header title
 * @param {string|Array} actions - Actions HTML or button configs
 * @returns {string} Panel header HTML
 */
export function createPanelHeader(title = '', actions = '') {
  // Convert actions array to HTML if needed
  const actionsHTML = Array.isArray(actions) 
    ? createPanelActions(actions) 
    : actions;
  
  return `
    <header class="panel__header">
      ${title ? `<h3 class="panel__title">${title}</h3>` : '<div class="panel__title-placeholder"></div>'}
      ${actionsHTML ? `<div class="panel__actions">${actionsHTML}</div>` : ''}
    </header>
  `;
}

/**
 * Helper to toggle panel collapsed state
 * 
 * @param {string|HTMLElement} panelOrId - Panel element or ID
 */
export function togglePanelCollapse(panelOrId) {
  const panel = typeof panelOrId === 'string' 
    ? document.getElementById(panelOrId)
    : panelOrId;
    
  if (!panel) return;
  
  panel.classList.toggle('panel--collapsed');
  const content = panel.querySelector('.panel__content');
  if (content) {
    content.classList.toggle('panel__content--collapsed');
  }
}

/**
 * Update panel content dynamically
 * 
 * @param {string|HTMLElement} panelOrId - Panel element or ID
 * @param {string} content - New content HTML
 */
export function updatePanelContent(panelOrId, content) {
  const panel = typeof panelOrId === 'string' 
    ? document.getElementById(panelOrId)
    : panelOrId;
    
  if (!panel) return;
  
  const contentEl = panel.querySelector('.panel__content');
  if (contentEl) {
    contentEl.innerHTML = content;
  }
}

/**
 * Update panel title dynamically
 * 
 * @param {string|HTMLElement} panelOrId - Panel element or ID
 * @param {string} title - New title text
 */
export function updatePanelTitle(panelOrId, title) {
  const panel = typeof panelOrId === 'string' 
    ? document.getElementById(panelOrId)
    : panelOrId;
    
  if (!panel) return;
  
  const titleEl = panel.querySelector('.panel__title');
  if (titleEl) {
    titleEl.textContent = title;
  }
}

/**
 * Migrate legacy panel to new system (backward compatibility)
 * Adds classes and attributes to make old panels work with new styles
 * 
 * @param {HTMLElement} element - Panel element to migrate
 */
export function migrateLegacyPanel(element) {
  if (!element || !Config.PANEL_MIGRATION?.enabled) return;
  
  // Check if already migrated
  if (element.dataset.migrated) return;
  
  // Add classes for new styling without breaking structure
  if (element.classList.contains('panel')) {
    element.dataset.theme = 'unified';
    element.dataset.migrated = 'true';
    
    // Determine if panel is in a layout (v2.0)
    const inLayout = element.closest('.panel-group, [data-split-container], .strategy-dual-panel, .portfolio-split-layout, .causality-split-layout, .insights-split-layout, .settings-grid-layout-simplified');
    if (inLayout && !element.classList.contains('panel-standalone')) {
      element.classList.add('panel-layout');
      element.dataset.panelBehavior = 'layout-safe';
    }
    
    const header = element.querySelector('.panel-header');
    if (header && !header.classList.contains('panel__header')) {
      header.classList.add('panel__header');
      const title = header.querySelector('h3, h4');
      if (title && !title.classList.contains('panel__title')) {
        title.classList.add('panel__title');
      }
    }
    
    const body = element.querySelector('.panel-body');
    if (body && !body.classList.contains('panel__content')) {
      body.classList.add('panel__content');
    }
  }
}

/**
 * Auto-migrate all panels in a container
 * 
 * @param {HTMLElement} container - Container to search for panels
 */
export function migrateAllPanels(container) {
  if (!container || !Config.PANEL_MIGRATION?.enabled) return;
  
  const panels = container.querySelectorAll('.panel:not([data-migrated])');
  panels.forEach(panel => migrateLegacyPanel(panel));
}

/**
 * Analyze panel configuration (v2.0 debug helper)
 * Logs panel behavior and container-type for debugging
 * 
 * @param {string|HTMLElement} panelOrId - Panel element or ID
 */
export function analyzePanelBehavior(panelOrId) {
  const panel = typeof panelOrId === 'string' 
    ? document.getElementById(panelOrId)
    : panelOrId;
    
  if (!panel) {
    console.warn('Panel not found');
    return;
  }
  
  const computed = window.getComputedStyle(panel);
  const containerType = computed.getPropertyValue('container-type');
  const behavior = panel.dataset.panelBehavior || 'auto';
  const inLayout = panel.closest('.panel-group, [data-split-container]');
  
  console.log('Panel Analysis:', {
    id: panel.id || 'unnamed',
    behavior,
    containerType: containerType || 'normal',
    inLayout: !!inLayout,
    classes: Array.from(panel.classList),
    isLayoutSafe: panel.classList.contains('panel-layout'),
    isStandalone: panel.classList.contains('panel-standalone'),
    hasResponsiveContent: !!panel.querySelector('.panel__inner')
  });
}

// Export all for convenience
export default {
  // Constants
  PANEL_VARIANTS,
  PANEL_SIZES,
  PANEL_LAYOUTS,
  PANEL_BEHAVIOR,
  // Main functions
  createPanel,
  createLayoutPanel,      // NEW v2.0
  createStandalonePanel,  // NEW v2.0
  createPanelGroup,
  createPanelActions,
  createPanelHeader,
  // DOM helpers
  togglePanelCollapse,
  updatePanelContent,
  updatePanelTitle,
  // Migration
  migrateLegacyPanel,
  migrateAllPanels,
  // Debug
  analyzePanelBehavior   // NEW v2.0
};
